import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {createSelector, Store} from '@ngrx/store';
import {ComponentBase, DialogService} from '@em/shared/ui';
import {validateEmail} from '@em/shared/ui';
import {
  finishCommonWizard,
  selectCommonWizard,
} from '@em/onboarding/data-access';
import {
  LocationUrlService,
  PASSWORD_MIN_LENGTH,
} from '@em/shared/util-configuration';
import {SessionService} from '@em/auth/data-access';
const mapError = (error: string): string => {
  if (error === 'email is already taken') return 'CREATE_ACCOUNT_EMAIL_IN_USE';
  return 'ERROR_UNKNOWN';
};

// Custom validator for password confirmation
const passwordMatchValidator =
  (passwordField: string, confirmPasswordField: string): ValidatorFn =>
  (formGroup: AbstractControl): ValidationErrors | null => {
    const password = formGroup.get(passwordField);
    const confirmPassword = formGroup.get(confirmPasswordField);

    if (!password || !confirmPassword) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      // Set error on confirmPassword control
      confirmPassword.setErrors({
        ...confirmPassword.errors,
        passwordMismatch: true,
      });
      return {passwordMismatch: true};
    } else {
      // Clear the passwordMismatch error if passwords match
      if (confirmPassword.errors) {
        delete confirmPassword.errors['passwordMismatch'];
        if (Object.keys(confirmPassword.errors).length === 0) {
          confirmPassword.setErrors(null);
        }
      }
    }
    return null;
  };

const selectCommonWizardState = createSelector(selectCommonWizard, (state) => ({
  state,
  error: state.error ? mapError(state.error) : null,
}));

@Component({
  selector: 'em-common-wizard',
  templateUrl: './common-wizard.component.html',
  styleUrls: ['./common-wizard.component.scss'],
  standalone: false,
})
export class CommonWizardComponent
  extends ComponentBase
  implements OnInit, OnDestroy
{
  private readonly _store = inject(Store);
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly sessionService = inject(SessionService);
  private readonly dialogService = inject(DialogService);
  private readonly locationUrlService = inject(LocationUrlService);

  readonly state$ = this._store.select(selectCommonWizardState);

  readonly form = this._fb.group(
    {
      email: ['', [Validators.required, validateEmail]],
      country: ['', Validators.required],
      language: ['', Validators.required],
      password: [
        '',
        [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)],
      ],
      confirmPassword: ['', Validators.required],
    },
    {validators: passwordMatchValidator('password', 'confirmPassword')},
  );

  constructor() {
    super();
  }

  ngOnInit() {
    this._subscribe(this.state$, ({state}) => {
      this.form.patchValue({
        email: state?.email,
        country: state?.country,
        language: state?.language,
      });
    });
  }

  canProcess() {
    return this.form.valid;
  }

  process() {
    if (!this.canProcess()) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {confirmPassword, ...others} = this.form.value;
    this._store.dispatch(
      finishCommonWizard({
        data: {...others, commonConfirmed: true},
        redirectAfterSave: true,
      }),
    );
  }
  logout() {
    this.dialogService
      .confirm({
        description: 'ONBOARDING_CANCEL_CONFIRM',
        confirmType: 'danger',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          // eslint-disable-next-line rxjs/no-nested-subscribe
          this.sessionService.logout().subscribe(() => {
            this.locationUrlService.forceChange('/auth/login');
          });
        }
      });
  }
}
